import React, { useEffect, useMemo, useState } from 'react'
import { ShieldCheck, X } from 'lucide-react'
import type { SpreadsheetSheetProtectionOptions } from '../../protocol/spreadsheetEngineProtocol'
import {
  buildSheetProtectionApplyPayload,
  buildSheetProtectionDialogPresentation,
  normalizeSheetProtectionDraftOptions,
  sheetProtectionIsEnabled,
  type SheetProtectionOptionKey,
} from '../../spreadsheet/sheetProtectionPresentation'
import './ExcelWorkbookChrome.css'

type ExcelSheetProtectionDialogProps = {
  currentProtection?: SpreadsheetSheetProtectionOptions | null
  sheetName?: string | null
  onClose: () => void
  onApply: (protection: SpreadsheetSheetProtectionOptions | null) => void
}

export function ExcelSheetProtectionDialog({
  currentProtection,
  sheetName,
  onClose,
  onApply,
}: ExcelSheetProtectionDialogProps) {
  const initiallyEnabled = useMemo(() => sheetProtectionIsEnabled(currentProtection), [currentProtection])
  const [enabled, setEnabled] = useState(initiallyEnabled || !currentProtection)
  const [draftOptions, setDraftOptions] = useState<SpreadsheetSheetProtectionOptions>(() => (
    normalizeSheetProtectionDraftOptions(currentProtection)
  ))
  const presentation = useMemo(() => buildSheetProtectionDialogPresentation({
    sheetName,
    draftOptions,
  }), [draftOptions, sheetName])

  useEffect(() => {
    const nextEnabled = sheetProtectionIsEnabled(currentProtection)
    setEnabled(nextEnabled || !currentProtection)
    setDraftOptions(normalizeSheetProtectionDraftOptions(currentProtection))
  }, [currentProtection])

  const toggleOption = (key: SheetProtectionOptionKey, checked: boolean) => {
    setDraftOptions((current) => ({ ...current, [key]: checked }))
  }

  const apply = () => {
    onApply(buildSheetProtectionApplyPayload({ enabled, draftOptions }))
    onClose()
  }

  const unprotect = () => {
    onApply(null)
    onClose()
  }

  return (
    <div className="fg-excel-format-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="fg-excel-sheet-protection-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={presentation.ariaLabel}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="fg-excel-format-dialog-header">
          <span className="fg-excel-cell-metadata-title">
            <ShieldCheck size={18} />
            <strong>{presentation.title}</strong>
          </span>
          <button type="button" className="fg-excel-icon-button" aria-label={presentation.closeAriaLabel} title={presentation.closeTitle} onClick={onClose}>
            <X size={16} />
          </button>
        </header>

        <div className="fg-excel-cell-metadata-range" title={presentation.sheetTitle}>
          {presentation.sheetLabel}
        </div>

        <div className="fg-excel-sheet-protection-body">
          <label className="fg-excel-sheet-protection-primary" title={presentation.primaryTitle}>
            <input
              type="checkbox"
              checked={enabled}
              title={presentation.primaryTitle}
              onChange={(event) => setEnabled(event.target.checked)}
            />
            <span>{presentation.primaryLabel}</span>
          </label>

          <fieldset className="fg-excel-sheet-protection-options" disabled={!enabled}>
            <legend>{presentation.optionsLegend}</legend>
            {presentation.options.map((option) => (
              <label key={option.key} title={option.title}>
                <input
                  type="checkbox"
                  checked={option.checked}
                  title={option.title}
                  onChange={(event) => toggleOption(option.key, event.target.checked)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </fieldset>
        </div>

        <footer className="fg-excel-cell-metadata-footer">
          <button type="button" className="fg-excel-cell-metadata-button" title={presentation.unprotectTitle} onClick={unprotect}>
            Unprotect
          </button>
          <button type="button" className="fg-excel-cell-metadata-button" title={presentation.cancelTitle} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="fg-excel-cell-metadata-button fg-excel-cell-metadata-button-primary" title={presentation.applyTitle} onClick={apply}>
            Apply
          </button>
        </footer>
      </section>
    </div>
  )
}
